import { CommonModule } from '@angular/common';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { DialogConfermaComponent } from '../dialog-conferma/dialog-conferma.component';
import { ImageCropperComponent } from 'ngx-image-cropper';
import type { ImageCroppedEvent } from 'ngx-image-cropper';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ExaMaterialModule } from 'app/main/modules/examaterial.module';
import { UtentiService } from '../../service/utente.service';
import { SessioneUtenteQuery, SessioneUtenteStore } from '../../state';
import { Utils } from '../../utils';

@Component({
  selector: 'app-upload-foto-profilo',
  templateUrl: './dialog-upload-foto-profilo.component.html',
  styleUrls: ['./dialog-upload-foto-profilo.component.scss'],
  standalone: true,
  imports: [CommonModule, ExaMaterialModule, ImageCropperComponent]
})
export class DialogUploadFotoProfiloComponent implements OnDestroy {
  @ViewChild(MatStepper, { static: true }) stepper: MatStepper;
  @ViewChild(ImageCropperComponent) imageCropper: ImageCropperComponent;

  stepperOrientation: 'horizontal' | 'vertical' = 'horizontal';
  isStep1ok: boolean = false;
  isStep2ok: boolean = false;

  imgPreview: string;

  croppedImage: any = '';
  private croppedBlob: Blob | null;
  isUploading: boolean;
  uploadError: string;
  isSaved: boolean;
  isDragging = false;
  maxSize = 1024 * 1024 * 10; // 10mb
  allowedMimeType = ['image/png', 'image/gif', 'image/jpeg', 'image/bmp', 'image/jpg'];
  urlImgProfilo?: string | null;
  private breakpointSubscription?: Subscription;
  private urlImgSubscription?: Subscription;
  private destroy$ = new Subject<void>();

  constructor(
    private utentiService: UtentiService,
    private sessioneUtenteQuery: SessioneUtenteQuery,
    private sessioneUtenteStore: SessioneUtenteStore,
    private breakpointObserver?: BreakpointObserver,
    private dialog?: MatDialog
  ) {
    this.urlImgSubscription = this.sessioneUtenteQuery.loggedInUser$.subscribe(
      ({ utente }) => (this.urlImgProfilo = utente?.url_img_profilo)
    );
    this.breakpointSubscription = this.breakpointObserver
      ?.observe('(max-width: 959px)')
      .subscribe(state => (this.stepperOrientation = state.matches ? 'vertical' : 'horizontal'));
  }

  ngOnDestroy() {
    this.breakpointSubscription?.unsubscribe();
    this.urlImgSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  goto(int) {
    this[`isStep${int}ok`] = true;
    setTimeout(() => {
      this.stepper.next();
    }, 10);
  }

  onFileDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
    this.onChangeFile(event.dataTransfer?.files?.[0]);
  }

  async onChangeFile(event: Event | File) {
    const input = event instanceof File ? null : (event.target as HTMLInputElement | null);
    const file = event instanceof File ? event : input?.files?.[0];
    if (!file) {
      return;
    }
    if (input) {
      input.value = '';
    }

    this.uploadError = '';
    if (!this.allowedMimeType.includes(file.type)) {
      this.uploadError = 'Seleziona un file immagine PNG, GIF, JPEG o BMP.';
      return;
    }
    if (file.size > this.maxSize) {
      this.uploadError = 'La dimensione massima del file è 10 MB.';
      return;
    }

    this.isSaved = false;
    this.imgPreview = await Utils.resizeImage(file, 600);
    this.goto(1);
  }

  async rotatePreview(dir: 'left' | 'right') {
    const cnv = document.createElement('canvas');
    const ctx = cnv.getContext('2d');
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Unable to load preview image'));
      img.src = this.imgPreview;
    });
    cnv.height = img.width;
    cnv.width = img.height;
    if (dir === 'right') {
      ctx.rotate((90 * Math.PI) / 180);
      ctx.translate(0, -cnv.width);
    } else {
      ctx.rotate((-90 * Math.PI) / 180);
      ctx.translate(-cnv.height, 0);
    }
    ctx.drawImage(img, 0, 0);
    this.imgPreview = cnv.toDataURL('image/jpeg', 100);
  }

  crop() {
    this.imageCropper.crop();
  }

  async imageCropped(event: ImageCroppedEvent) {
    this.croppedImage = await Utils.convertBlobUrlToBase64(event.objectUrl);
    this.croppedBlob = event.blob ?? null;
    this.isSaved = false;
    this.goto(2);
  }

  uploadProfilo() {
    if (this.isUploading || this.isSaved || !this.croppedImage) {
      return;
    }

    const mimeType = this.croppedBlob?.type || this.croppedImage.match(/^data:(.*?);/)[1];
    const ext = mimeType.split('/')[1];
    const file = this.croppedBlob
      ? new File([this.croppedBlob], `profilo.${ext}`, { type: mimeType })
      : Utils.base64ToFile(this.croppedImage, { ext });
    const formData = new FormData();
    formData.append('file', file);
    this.isUploading = true;
    this.uploadError = '';
    this.utentiService.uploadFotoProfilo(formData).subscribe({
      next: () => {
        this.isUploading = false;
        this.isSaved = true;
        this.isStep1ok = false;
        this.isStep2ok = false;
        this.imgPreview = undefined;
        this.croppedImage = '';
        this.croppedBlob = null;
        this.stepper?.reset();
        this.aggiornaSessione();
      },
      error: () => {
        this.isUploading = false;
        this.uploadError = 'Impossibile salvare l’immagine del profilo. Riprova.';
      }
    });
  }

  eliminaFoto() {
    if (this.isUploading) {
      return;
    }
    this.dialog
      ?.open(DialogConfermaComponent, {
        data: { titolo: 'Elimina foto profilo', messaggio: 'Vuoi eliminare la foto profilo?', buttonOkText: 'Elimina' }
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result !== 1) {
          return;
        }
        this.isUploading = true;
        this.uploadError = '';
        this.isSaved = false;
        this.utentiService
          .eliminaFotoProfilo()
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.isUploading = false;
              this.aggiornaSessione(true);
            },
            error: () => {
              this.isUploading = false;
              this.uploadError = 'Impossibile eliminare la foto profilo. Riprova.';
            }
          });
      });
  }

  private aggiornaSessione(rimossa = false) {
    this.utentiService
      .getUtente()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => {
          const utente = data?.utente;
          const url = utente?.url_img_profilo;
          if (!utente || !(rimossa || url)) {
            return;
          }

          this.urlImgProfilo = url || null;
          const loggedUser = this.sessioneUtenteQuery.loggedUser();
          if (loggedUser) {
            this.sessioneUtenteStore.login({ ...loggedUser, url_img_profilo: this.urlImgProfilo });
          }
        },
        error: () => (this.uploadError = 'Operazione eseguita, aggiornamento non riuscito: ricarica la pagina.')
      });
  }
}
